import { ChevronLeftIcon } from "@heroicons/react/24/solid";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { requestPasswordDecrypto, requestPasswordEncrypto } from "../../api/Decrypto.api";
import { Spiner } from "../../components/spinner/Spiner";
import { alertOpen, AlertValue } from "../../features/alert.slice";
import { auth } from "../../firebase";
import companiesQuery from "../../firestore/Companies.query";
import userQuery from "../../firestore/User.query";
import { EncryptedKey, Keys } from "../../model/Keys";
import { Result } from "../../model/Result";
import { initialUserDataInfo, Passcode, User, UserData } from "../../model/Users";
import { Company } from "../../model/Companies";
import Button from "../../components/button/Button";
import { EditPasswordModal, PasscodeEdit } from "./EditPasswordModal";

export const EditPassword = () => {

  const { userId } = useParams<{ userId: string }>();

  const [userData, setUserData] = useState<UserData | undefined>();
  const [isLoad, setIsLoad] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [company, setCompany] = useState<Company>();
  const [user, setUser] = useState<User>();
  const [isChanged, setIsChanged] = useState<boolean>(true);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ログイン中の団体管理者情報
  const currentUser = auth.currentUser;
  const tenantId = currentUser?.tenantId;

  useEffect(() => {
    const f = async () => {
      if(userId){
        // 団体情報を取得
        const company = await companiesQuery.getCompany(currentUser?.tenantId!);
        setCompany(company);

        const userDisplayId = userId?.replace(company.displayId, '');

        const user = initialUserDataInfo;
        setUser(user);
        // 利用者情報を初期化
        user.id = userId;
        // パスコード取得(暗号化済み)
        await userQuery.getPasscode(currentUser?.tenantId!, userId).then((resolve) => {
          user.encryptedPasscode = resolve[0].encryptedPasscode;
        });
        // パスワード復号化
        const keys: Keys = {
          encryptedPasscode: user?.encryptedPasscode!,
          userId: userDisplayId
        }
        const result: Result = await requestPasswordDecrypto([keys], company.displayId);
        if (result.success) {
          user.decryptedPasscode = result.data[0].decryptedPasscode.replace(company.displayId, '');
        }
        setUserData(user);
        setIsLoad(false);
      }else{
        dispatch(
          alertOpen(AlertValue("ネットワークに接続できません。", "danger"))
        );
      }
    }
    f();
  },[currentUser?.tenantId, dispatch, userId, isChanged]);

  const onSubmit = async (data: PasscodeEdit) => {
    setIsLoad(true);
    // パスワードの更新
    const passcode = data.passcode;
    const keys: EncryptedKey = {
      passcode: passcode,
      userId: user?.id!
    }
    const encryptedPasscode = await requestPasswordEncrypto(
      [keys],
      company?.displayId!
    );
    if(encryptedPasscode.success) {
      const responseData = encryptedPasscode.data as Keys[];
      const dbPasscodeList = await userQuery.getPasscode(tenantId!, user?.id!);
      if(dbPasscodeList.length > 0) {
        const updatePasscode: Passcode = {
          id: dbPasscodeList[0].id,
          userId: user?.id!,
          encryptedPasscode: responseData[0].encryptedPasscode,
          createAt: dbPasscodeList[0].createAt,
        }
        await userQuery.updatePasscode(tenantId!, user?.id!, updatePasscode);
      } else {
        // パスワード新規登録
        const newPasscode: Passcode = {
          id: "",
          userId: user?.id!,
          encryptedPasscode: responseData[0].encryptedPasscode,
          createAt: new Date(),
        }
        await userQuery.addPasscode(tenantId!, user?.id!, newPasscode);
      }
      dispatch(
        alertOpen(AlertValue("パスワードを変更しました", "success"))
      );
    } else {
      console.error(encryptedPasscode.data);
      dispatch(
        alertOpen(AlertValue("パスワード変更に失敗しました。", "danger"))
      );
    }
    setIsChanged(!isChanged);
    toggle();
  }

  const toggle = () => {
    setIsOpen(!isOpen);
  }

  return (<>
    <div className="flex h-full">
      <div className="relative z-0 flex flex-1 overflow-hidden">
        <main className="relative z-0 flex-1 overflow-y-auto focus:outline-none xl:order-last">
          {/* Breadcrumb */}
          <nav className="flex items-start px-4 py-3">
            <div className="inline-flex items-center space-x-3 text-sm font-medium text-gray-900 cursor-pointer">
              <span onClick={() => navigate(`/userList/${userData?.id}/info`)} className="flex items-center">
                <ChevronLeftIcon className="-ml-2 h-5 w-5 text-gray-400 mr-3" />利用者情報へ</span>
            </div>
          </nav>
          <article>{isLoad === true ? <Spiner/>
            : (
              <div className= {"flex flex-col space-y-4 px-8"}>
                <ul>
                  <li>利用者番号: {userId?.replace(company?.displayId ?? '', '')}</li>
                  <li>パスワード: {userData?.decryptedPasscode}</li>
                </ul>
                <div>
                  <Button
                    size={"sm"}
                    onClick={toggle}
                    children={"パスワード変更"}
                  />
                  <EditPasswordModal
                    children={undefined}
                    open={isOpen}
                    toggle={toggle}
                    closeOnClickOutside={false}
                    onSubmit={onSubmit}
                  />
                </div>
              </div>
            )}
          </article>
        </main>
      </div>
    </div>
  </>);
}